/**
 * A MIDI file written down as notation.
 *
 * Every step of this is a decision, and none of them can be checked against the file:
 * MIDI records when keys went down, not what was written. It does not say how a note is
 * spelled, where the bar lines are, which hand played it, or whether a quarter note that
 * lasted a little under a quarter was meant to be short or merely played that way. So the
 * rules are stated once, here, and applied the same way everywhere:
 *
 * - A file that was played rather than written is first put back on a beat
 *   ({@link unperformed}): its beats, bars and tempo are read from the performance, and
 *   said so in the warnings. A file written on its own grid is left as it is.
 * - Everything is snapped to a grid, a 16th note by default, in both onset and length.
 *   Nothing shorter than the grid can be written, and a note under it is lengthened to it
 *   rather than dropped.
 * - A track played from a keyboard with two hands is written on two staves, the line
 *   between the hands being read from the notes ({@link handsOf}). Any other track is one
 *   staff, in the clef its notes sit in.
 * - A staff that holds one note while it plays others is written in two voices
 *   ({@link voicesOf}), and no more: what a third would hold is tied into the nearer of the
 *   two. On a drum staff every note is let go at the next attack, since a drum is not held.
 * - A note let go exactly on the grid is taken at its word, as is one a written file lets go
 *   a tick of its own before it. One let go off it was played,
 *   and a grid step of its length is not: notes struck together and let go within a step
 *   of each other are a chord, and a note let go a step into the next is cut there, as
 *   legato is written. On a staff a hand played, a note ends at the attack nearest to where
 *   it was let go ({@link released}).
 * - Spelling comes from the key signature the file states, so a piece in five flats is not
 *   written in sharps. A written file that states none is spelled from C; a played one is
 *   spelled from the key its notes fit best.
 * - Nothing is invented. There are no slurs, no dynamics, no articulation and no phrasing
 *   in a MIDI file, so there are none in what comes out; what is here is what was played.
 */

import { PPQ, plain, ticksOf } from "../document/duration";
import type { NoteType } from "../document/duration";
import { addPart, createScore, emptyPartMeasure } from "../document/create";
import { BASS, TREBLE } from "../document/types";
import type { Clef, PartMeasure, Score } from "../document/types";
import type { MeasureId } from "../document/ids";
import { DRUM_KIT } from "../editor/kits";
import { handsOf, keyboard } from "./hands";
import { unperformed } from "./played";
import { planBars } from "./plan";
import type { PlannedBar } from "./plan";
import { released } from "./released";
import { clipped, piecesOf, spansOf } from "./spans";
import type { ScaledNote } from "./spans";
import { barsOf } from "./staves";
import type { VoiceBars } from "./staves";
import { voicesOf } from "./voicing";
import { eventsOf, silent } from "./written";
import type { MidiFile, MidiNote, MidiTrack } from "./read";

/** The channel General MIDI reserves for drums, counted from zero. */
const DRUM_CHANNEL = 9;

const PERCUSSION_CLEF: Clef = { sign: "percussion", line: 3, octaveChange: 0 };

export interface MidiImportOptions {
  /** The shortest note value the transcription may write. A 16th unless said otherwise. */
  readonly grid?: NoteType;
}

/** The outcome of writing a file down, shaped as the MusicXML import's is. */
export type MidiImport =
  | { readonly ok: true; readonly score: Score; readonly warnings: readonly string[] }
  | { readonly ok: false; readonly reason: string };

interface Line {
  readonly name: string;
  readonly program: number | null;
  readonly percussion: boolean;
  readonly notes: readonly ScaledNote[];
}

/**
 * One line of music per track -- except in a format 0 file, where every instrument is on
 * one track and told apart only by its channel, and the channels are separated here.
 */
function linesOf(
  tracks: readonly MidiTrack[],
  snap: (tick: number) => number,
  on: (note: MidiNote, end: number) => boolean,
  unit: number,
): Line[] {
  const played = tracks.filter((track) => track.notes.length > 0);
  const split =
    played.length === 1 && new Set((played[0] as MidiTrack).notes.map((n) => n.channel)).size > 1
      ? [...new Set((played[0] as MidiTrack).notes.map((n) => n.channel))]
          .sort((first, second) => first - second)
          .map((channel) => ({
            name: `Channel ${channel + 1}`,
            program: (played[0] as MidiTrack).program,
            notes: (played[0] as MidiTrack).notes.filter((n) => n.channel === channel),
          }))
      : played.map((track, index) => ({
          name: track.name ?? `Part ${index + 1}`,
          program: track.program,
          notes: track.notes,
        }));

  return split.map((line) => ({
    name: line.name.trim() === "" ? "Part" : line.name.trim(),
    program: line.program,
    percussion: line.notes.every((n: MidiNote) => n.channel === DRUM_CHANNEL),
    notes: line.notes.map((n: MidiNote) => {
      const start = snap(n.start);
      const end = snap(n.start + n.ticks);
      return { key: n.key, start, ticks: Math.max(unit, end - start), played: !on(n, end) };
    }),
  }));
}

function clefFor(line: Line): Clef {
  if (line.percussion) return PERCUSSION_CLEF;
  const keys = line.notes.map((n) => n.key).sort((first, second) => first - second);
  const middle = keys[Math.floor(keys.length / 2)] ?? 60;
  return middle < 57 ? BASS : TREBLE;
}

/** The staves a line is written on, each with its clef and its notes, the upper first. */
function stavesOf(line: Line): readonly { clef: Clef; notes: readonly ScaledNote[] }[] {
  const hands = !line.percussion && keyboard(line.program) ? handsOf(line.notes) : null;
  if (hands === null) return [{ clef: clefFor(line), notes: line.notes }];
  return [
    { clef: TREBLE, notes: hands.right },
    { clef: BASS, notes: hands.left },
  ];
}

/** Write a file down. `reason` comes back instead when there is nothing to write. */
export function transcribe(given: MidiFile, options: MidiImportOptions = {}): MidiImport {
  const { file, warnings: retimed } = unperformed(given);
  const unit = ticksOf(plain(options.grid ?? "16th"));
  const division = file.division > 0 ? file.division : PPQ;
  const snap = (tick: number): number =>
    Math.max(0, Math.round((tick * PPQ) / division / unit) * unit);

  // Whether a note is let go exactly where the grid puts its end: written, not played. A file
  // written on its own grid may let a note go one of its ticks early, as a sequencer does so
  // that the note does not run into the next; that is the grid too.
  const early = file === given ? PPQ / division : 0;
  const on = (note: MidiNote, end: number): boolean => {
    const short = end - ((note.start + note.ticks) * PPQ) / division;
    return short >= 0 && short <= early;
  };

  const lines = linesOf(file.tracks, snap, on, unit);
  if (lines.length === 0)
    return { ok: false, reason: "this file has no notes in it to write down" };

  const ends = lines.flatMap((line) => line.notes.map((n) => n.start + n.ticks));
  const plan = planBars(file, Math.max(unit, ...ends), snap);
  const bars = plan.bars;
  const last = bars[bars.length - 1] as PlannedBar;
  const until = last.start + last.ticks;

  const warnings = new Set([...retimed, ...plan.warnings]);
  const warn = (text: string): void => {
    warnings.add(text);
  };

  const first = bars[0] as PlannedBar;
  let score = createScore(file.title ?? "Untitled", {
    time: first.time ?? undefined,
    key: first.key ?? undefined,
    measures: bars.length,
  });
  score = {
    ...score,
    measures: score.measures.map((measure, index) => {
      const planned = bars[index] as PlannedBar;
      const length = index === 0 ? null : measure.length;
      return { ...measure, time: planned.time, key: planned.key, tempo: planned.tempo, length };
    }),
  };

  for (const line of lines) {
    const kit = line.percussion ? DRUM_KIT : null;
    const staves = stavesOf(line);
    score = addPart(score, line.name, {
      clefs: staves.map((staff) => staff.clef),
      lines: 5,
      instrument: {
        name: line.name,
        midiProgram: line.program,
        percussion: kit,
      },
    });
    const part = score.parts[score.parts.length - 1] as Score["parts"][number];
    const written = staves.map((staff) =>
      (kit === null ? voicesOf(released(staff.notes), bars, unit) : [clipped(staff.notes)]).map(
        (notes): VoiceBars => {
          const pieces = piecesOf(spansOf(notes, until, unit), bars);
          return pieces.map((here, index) =>
            silent(here) ? null : eventsOf(here, bars, index, kit, warn),
          );
        },
      ),
    );
    const laid = barsOf(written, bars.length);
    const measures: Record<MeasureId, PartMeasure> = {};
    score.measures.forEach((measure, index) => {
      measures[measure.id] = { ...emptyPartMeasure(staves.length), voices: laid[index] ?? [] };
    });
    score = {
      ...score,
      parts: score.parts.map((candidate) =>
        candidate.id === part.id ? { ...candidate, measures } : candidate,
      ),
    };
  }

  return { ok: true, score, warnings: [...warnings] };
}
